// Umbrella "Inventory" hub. Renders a Settings-style pill row of sub-tabs
// (Items · Hardware · SIP Trunks · Manufacturers · DID Master); each pill
// deep-links to a child route via React Router. The child page
// (`<Outlet />`) renders below the pill row.
//
// Super Admins see an "Edit layout" button that lets them drag-drop the
// pills into any order. Order is persisted globally in the settings doc
// under `submenu_orders.inventory` and read back on mount.
//
// Adding a new sub-tab later is a one-line addition to the `DEFAULT_TABS`
// array below plus a matching nested <Route> in App.js.

import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Boxes, Cpu, Factory, Radio, Phone, Pencil, Check, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

// Sub-nav tabs. Each entry: `to` (route), `label`, `icon` (lucide),
// `staffOnly` (optional). Order here = default display order.
const DEFAULT_TABS = [
  { to: "/inventory-hub",                 label: "Items",         icon: Boxes,   end: true },
  { to: "/inventory-hub/hardware",        label: "Endpoints",     icon: Cpu,     end: false, staffOnly: true },
  { to: "/inventory-hub/sip-trunks",      label: "SIP Trunks",    icon: Radio,   end: false, staffOnly: true },
  { to: "/inventory-hub/manufacturers",   label: "Manufacturers", icon: Factory, end: false, staffOnly: true },
  { to: "/inventory-hub/did-master",      label: "DID Master",    icon: Phone,   end: false, staffOnly: true },
  { to: "/inventory-hub/network-keys",    label: "Network Assessment Keys", icon: KeyRound, end: false, staffOnly: true },
];

const MENU_KEY = "inventory";  // key used in settings.submenu_orders[MENU_KEY]

export default function InventoryHub() {
  const { user } = useAuth();
  const role = ((user?.base_role || user?.role) || "").toLowerCase();
  const isStaff = role === "staff" || role === "super_admin";
  const isSuper = user?.role === "super_admin";

  // Persisted order. `null` while loading; array once fetched.
  const [savedOrder, setSavedOrder] = useState(null);
  const [editMode, setEditMode]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const dragKeyRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const persisted = r.data?.submenu_orders?.[MENU_KEY];
      setSavedOrder(Array.isArray(persisted) ? persisted : []);
    }).catch(() => setSavedOrder([]));
  }, []);

  // Merge persisted order with defaults so newly-added tabs still appear
  // at the tail even if the admin never re-saved after a code deploy.
  const orderedTabs = useMemo(() => {
    const persisted = savedOrder || [];
    const byRoute = Object.fromEntries(DEFAULT_TABS.map((t) => [t.to, t]));
    const ordered = persisted.map((r) => byRoute[r]).filter(Boolean);
    const missing = DEFAULT_TABS.filter((t) => !persisted.includes(t.to));
    return [...ordered, ...missing];
  }, [savedOrder]);

  const visible = orderedTabs.filter((t) => !t.staffOnly || isStaff);

  const saveOrder = async (nextOrder) => {
    setSaving(true);
    try {
      const current = (await api.get("/settings")).data || {};
      const nextSubmenus = { ...(current.submenu_orders || {}), [MENU_KEY]: nextOrder.map((t) => t.to) };
      await api.put("/settings", { submenu_orders: nextSubmenus });
      setSavedOrder(nextOrder.map((t) => t.to));
      toast.success("Order saved");
    } catch (e) {
      toast.error("Failed to save order");
    } finally { setSaving(false); }
  };

  const reorder = (targetRoute) => {
    const src = dragKeyRef.current;
    if (!src || src === targetRoute) return;
    const list = [...orderedTabs];
    const from = list.findIndex((t) => t.to === src);
    const to   = list.findIndex((t) => t.to === targetRoute);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    // Local optimistic update — persist immediately so the user sees the
    // final answer without a separate "Save" click.
    setSavedOrder(list.map((t) => t.to));
    saveOrder(list);
    dragKeyRef.current = null; setDragOver(null);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-5 space-y-4" data-testid="inventory-hub">
      {/* Settings-style pill row — mirrors the sub-tab UI in Settings.jsx
          so the platform's menu/submenu treatment is consistent everywhere. */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2"
             data-testid="inventory-hub-tabs">
          {visible.map((t) => {
            const Icon = t.icon;
            const draggable = editMode;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                draggable={draggable}
                onDragStart={draggable ? (e) => {
                  dragKeyRef.current = t.to;
                  try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", t.to); } catch { /* noop */ }
                } : undefined}
                onDragOver={draggable ? (e) => { e.preventDefault(); setDragOver(t.to); } : undefined}
                onDragLeave={draggable ? () => setDragOver((p) => (p === t.to ? null : p)) : undefined}
                onDrop={draggable ? () => reorder(t.to) : undefined}
                onDragEnd={() => { dragKeyRef.current = null; setDragOver(null); }}
                onClick={editMode ? (e) => e.preventDefault() : undefined}
                data-testid={`inv-hub-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    isActive && !editMode
                      ? "border-primary bg-white text-primary shadow-sm"
                      : "border-transparent bg-white/70 text-slate-600 hover:text-slate-900"
                  } ${draggable ? "cursor-grab" : ""} ${dragOver === t.to ? "ring-2 ring-primary/50" : ""}`
                }>
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </NavLink>
            );
          })}
        </div>
        {isSuper && (
          <button
            type="button"
            onClick={() => setEditMode((v) => !v)}
            disabled={saving}
            data-testid="inventory-hub-edit-layout"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-primary/40 hover:text-slate-900 disabled:opacity-50">
            {editMode ? <><Check className="h-3.5 w-3.5" /> Done</> : <><Pencil className="h-3.5 w-3.5" /> Edit layout</>}
          </button>
        )}
      </div>
      {editMode && (
        <p className="text-[11px] text-slate-500" data-testid="inventory-hub-edit-hint">
          Drag pills to reorder. Order saves automatically and applies for everyone.
        </p>
      )}
      <Outlet />
    </div>
  );
}
